import React from "react";
import {Box, Divider, Paper, Tooltip, Typography} from "@mui/material";
import AssignmentTwoToneIcon from "@mui/icons-material/AssignmentTwoTone";
import AssignmentReturnedTwoToneIcon from "@mui/icons-material/AssignmentReturnedTwoTone";
import AssignmentTurnedInTwoToneIcon from "@mui/icons-material/AssignmentTurnedInTwoTone";
import AssistantTwoToneIcon from "@mui/icons-material/AssistantTwoTone";
import HourglassFullTwoToneIcon from "@mui/icons-material/HourglassFullTwoTone";
import ArchiveTwoToneIcon from "@mui/icons-material/ArchiveTwoTone";
import SendTwoToneIcon from "@mui/icons-material/SendTwoTone";
import BathtubTwoToneIcon from "@mui/icons-material/BathtubTwoTone";
import MailTwoToneIcon from "@mui/icons-material/MailTwoTone";
import PhoneCallbackTwoToneIcon from "@mui/icons-material/PhoneCallbackTwoTone";
import HealingTwoToneIcon from "@mui/icons-material/HealingTwoTone";
import LabelTwoToneIcon from "@mui/icons-material/LabelTwoTone";
import ThumbUpAltTwoToneIcon from "@mui/icons-material/ThumbUpAltTwoTone";
import ThumbDownAltTwoToneIcon from "@mui/icons-material/ThumbDownAltTwoTone";
import clsx from "clsx";

const primaryQueues = [
	{
		value: "Nuovo",
		label: "Nuovi",
		description: "In ingresso",
		countKey: "Nuovi",
		color: "#00a8e8",
		icon: <AssistantTwoToneIcon/>,
	},
	{
		value: "Aperto",
		label: "Aperti",
		description: "Da lavorare",
		countKey: "Aperti",
		color: "#4285f4",
		icon: <AssignmentTwoToneIcon/>,
	},
	{
		value: "Preso in carico",
		label: "Presi in carico",
		description: "Assegnati",
		countKey: "Presi in carico",
		color: "#7e57c2",
		icon: <AssignmentReturnedTwoToneIcon/>,
	},
	{
		value: "In attesa",
		label: "In attesa",
		description: "Risposta esterna",
		countKey: "In attesa",
		color: "#f9ab00",
		icon: <HourglassFullTwoToneIcon/>,
	},
	{
		value: "Risolto",
		label: "Risolti",
		description: "Chiusi",
		countKey: "Risolti",
		color: "#34a853",
		icon: <AssignmentTurnedInTwoToneIcon/>,
	},
	{
		value: "Ripristinato",
		label: "Ripristinati",
		description: "Riaperti",
		countKey: "Ripristinati",
		color: "#009688",
		icon: <HealingTwoToneIcon/>,
	},
];

const workQueues = [
	{
		value: "Le mie richieste",
		label: "Le mie richieste",
		description: "Ultime 72 ore",
		countKey: "Le mie richieste",
		color: "#1a73e8",
		icon: <SendTwoToneIcon/>,
		tooltip: "Qui trovi i tuoi ticket effettuati nelle ultime 72 ore.",
	},
	{
		value: "In gestione",
		label: "In gestione",
		description: "Bloccati da operatori",
		countKey: "In gestione",
		color: "#8e24aa",
		icon: <BathtubTwoToneIcon/>,
	},
	{
		value: "Risposte utente",
		label: "Risposte utente",
		description: "Utente ha risposto",
		countKey: "Risposte Utente",
		color: "#f57c00",
		icon: <MailTwoToneIcon/>,
	},
	{
		value: "Richieste chiamata",
		label: "Richieste chiamata",
		description: "Contatto telefonico",
		countKey: "Richieste chiamata",
		color: "#ea4335",
		icon: <PhoneCallbackTwoToneIcon/>,
	},
];

const priorityFilters = [
	{value: "Bassa", label: "Bassa", color: "#34a853"},
	{value: "Media", label: "Media", color: "#f9ab00"},
	{value: "Alta", label: "Alta", color: "#ea4335"},
	{value: "Urgente", label: "Urgente", color: "#8e24aa"},
];

const resultFilters = [
	{value: "Positivo", label: "Positivo", color: "#34a853", icon: <ThumbUpAltTwoToneIcon/>},
	{value: "Negativo", label: "Negativo", color: "#ea4335", icon: <ThumbDownAltTwoToneIcon/>},
];

const formatCount = (value) => {
	if (value === undefined || value === null || Number.isNaN(value)) {
		return 0;
	}
	if (Number(value) >= 1000) {
		return `${Math.floor(Number(value) / 1000)}K`;
	}
	return value;
};

const QueueButton = ({classes, item, active, count, onClick}) => {
	const content = (
		<Box
			component="button"
			type="button"
			className={clsx(classes.queueButton, {[classes.queueButtonActive]: active})}
			style={{"--queue-color": item.color}}
			onClick={onClick}
		>
			<span className={classes.queueIcon}>{item.icon}</span>
			<span className={classes.queueCopy}>
				<Typography component="span" className={classes.queueItemTitle}>{item.label}</Typography>
				<Typography component="span" className={classes.queueItemDescription}>{item.description}</Typography>
			</span>
			<span className={classes.queueCount}>{formatCount(count)}</span>
		</Box>
	);

	return item.tooltip ? <Tooltip title={item.tooltip}>{content}</Tooltip> : content;
};

const FilterButton = ({classes, item, active, onClick}) => (
	<Box
		component="button"
		type="button"
		className={clsx(classes.queueButton, {[classes.queueButtonActive]: active})}
		style={{"--queue-color": item.color}}
		onClick={onClick}
	>
		<span className={classes.queueIcon}>{item.icon || <LabelTwoToneIcon/>}</span>
		<span className={classes.queueCopy}>
			<Typography component="span" className={classes.queueItemTitle}>{item.label}</Typography>
			<Typography component="span" className={classes.queueItemDescription}>
				{item.description || (active ? "Filtro attivo" : "Filtra priorita")}
			</Typography>
		</span>
	</Box>
);

const TicketQueuePanel = React.forwardRef(function TicketQueuePanel({
	classes,
	counts,
	status,
	priority,
	esito,
	onStatusChange,
	onPriorityChange,
	onEsitoChange,
	onArchiveClick,
}, ref) {
	return (
	<Paper ref={ref} elevation={0} className={classes.queuePanel} component="aside">
		<div className={classes.queueHeader}>
			<div>
				<Typography className={classes.queueTitle}>Code</Typography>
				<Typography className={classes.queueSubtitle}>Stato e urgenza</Typography>
			</div>
		</div>

		<div className={classes.queueSection}>
			<Typography className={classes.queueSectionTitle}>Stati</Typography>
			{primaryQueues.map((item) => (
				<QueueButton
					key={item.value}
					classes={classes}
					item={item}
					active={status === item.value}
					count={counts?.[item.countKey]}
					onClick={() => {
						onEsitoChange(null);
						onStatusChange(item.value);
					}}
				/>
			))}
		</div>

		{(status === "Risolto" || esito) && (
			<div className={classes.queueSection}>
				<Typography className={classes.queueSectionTitle}>Esito</Typography>
				{resultFilters.map((item) => (
					<FilterButton
						key={item.value}
						classes={classes}
						item={item}
						active={esito === item.value}
						onClick={() => {
							onStatusChange("Risolto");
							onEsitoChange(esito === item.value ? null : item.value);
						}}
					/>
				))}
			</div>
		)}

		<Divider className={classes.queueDivider}/>

		<div className={classes.queueSection}>
			<Typography className={classes.queueSectionTitle}>Lavoro</Typography>
			{workQueues.map((item) => (
				<QueueButton
					key={item.value}
					classes={classes}
					item={item}
					active={status === item.value}
					count={counts?.[item.countKey]}
					onClick={() => {
						onEsitoChange(null);
						onStatusChange(item.value);
					}}
				/>
			))}
			<FilterButton
				classes={classes}
				item={{
					label: "Archivio",
					description: "Storico completo",
					color: "#607d8b",
					icon: <ArchiveTwoToneIcon/>,
				}}
				active={false}
				onClick={onArchiveClick}
			/>
		</div>

		<Divider className={classes.queueDivider}/>

		<div className={classes.queueSection}>
			<Typography className={classes.queueSectionTitle}>Priorita</Typography>
			{priorityFilters.map((item) => (
				<FilterButton
					key={item.value}
					classes={classes}
					item={item}
					active={priority === item.value}
					onClick={() => onPriorityChange(priority === item.value ? "" : item.value)}
				/>
			))}
		</div>
	</Paper>
	);
});

export default React.memo(TicketQueuePanel);
